import { useState } from 'react';

import { Checkbox } from '@components/checkbox';
import { HideContainer } from '@components/hide-container';
import { IconReplace } from '@components/icons/replace';
import { SelectOption } from '@components/select';
import { InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { SingleField } from '@components/text-editors/addons/single-field';
import { useScopedTranslate } from '@locale/translate';

import { createJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionDetailed, JourneyActionValuesTypes } from 'api/types';
import { TimeSavedContainer } from 'ui/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';

type ConvertDocumentData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  document: FieldMappingEditorValue[];
  name: FieldMappingEditorValue;
} & TimeSavedData;

const Form = createActionForm<ConvertDocumentData>();

export const SidebarContent = (props: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction(journeyActionId);

  if (isLoading || !data) {
    return (
      <ActionSidebarLoading
        title={t('convertDocument')}
        icon={IconReplace}
        iconColor="elementBlue100"
        {...props}
      />
    );
  }

  return (
    <Sidebar>
      <ActionHeader
        title={t('convertDocument')}
        icon={IconReplace}
        iconColor="elementBlue100"
        {...props}
      >
        <InfoButton>
          <Info />
        </InfoButton>
      </ActionHeader>
      <Sidebar.Content>
        <ConvertDocument journeyActionId={journeyActionId} action={data} />
      </Sidebar.Content>
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('aboutConvert')} />
      <Text text={t('convertedAccepted')} />
      <Text text={t('convertingExample')} />
      <Text text={t('youCanSelect')} />
    </Stack>
  );
};

const ConvertDocument = ({
  journeyActionId,
  action,
}: {
  journeyActionId: string;
  action: JourneyActionDetailed;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isRequired = formStore.useFieldValue('executionMethod') === 'automatic';
  const values = action.values as JourneyActionValuesTypes['convert_document'];
  const [rename, setRename] = useState(Boolean(values?.name));
  const isFormDirty = formStore.useIsDirty();
  const isDirty = isFormDirty || rename !== Boolean(values?.name);
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const uploadMutation = useUploadFile(
    createJourneyActionDocumentPath(action.journey, journeyActionId),
    true
  );
  const fieldMappingId = getFieldMappingId(action.journey);
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
  });
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['file'],
  });

  const handleSave = (data: ConvertDocumentData) =>
    editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: 'convert_document',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        document: data.document[0] ?? {},
        name: rename ? data.name : undefined,
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });

  const document = values?.document?.value ? [values?.document] : [];

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
            hideLabel
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('document')}>
          <Form.FieldMappingFileListField
            id="document"
            label={t('document')}
            defaultValue={document}
            extensions={[SingleField]}
            variableGroups={variableGroups}
            onSelect={uploadMutation.mutateAsync}
            fileType="document"
            validators={isRequired ? ['required'] : []}
            fieldMappingId={fieldMappingId}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('advanced')} defaultClosed={!rename}>
          <Stack space={4}>
            <Checkbox label={t('renameOutput')} onChange={setRename} defaultValue={rename} />
            {rename && (
              <Form.FieldMappingTextEditor
                id="name"
                plaintext
                singleLine
                label={t('fileOutputName')}
                defaultValue={values?.name}
                variableGroups={baseVariableGroups}
                validators={['required']}
                fieldMappingId={fieldMappingId}
              />
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <TimeSavedContainer {...action.timeSaved} />
      </Stack>
      <ActionFooter action={action} isDisabled={!isDirty || editMutation.isLoading} form={Form} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    convertDocument: 'Convert document to PDF',
    aboutConvert: 'About converting document',
    convertedAccepted: 'Convert documents to .PDF\nAccepted file types .docx .doc .pages .odt',
    convertingExample:
      'Converting documents can for example be used for converting contracts before sending for e-signing as e-signing requires documents to be in PDF format',
    youCanSelect: 'You can select a document created in the journey or manually upload a document',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    document: 'Document',

    description: 'Description',
    title: 'Convert document',
    template: 'Template',
    advanced: 'Advanced',
    renameOutput: 'Rename document output',
    fileOutputName: 'File output name',
  },
};
